import { Skeleton } from '../ui/skeleton.js';

/**
 * Content-area loader rendered while the LicenseProvider hydrates on
 * cold mount (the very first `/v1/license` fetch). Rendered INSIDE
 * `IframeShell` — the chrome (sidebar + header + footer) is already
 * up, so we only fill the main outlet with shape-matching skeletons.
 *
 * Mirrors legacy `wf-audit/src/slices/common/components/LoadingScreen.tsx`
 * adapted to the new shell — the legacy did its own full-page paint
 * because Next.js streams the chrome late; in the new SPA the
 * chrome renders synchronously off the JWT.
 */
export function LoadingScreen(): JSX.Element {
  return (
    <div className="relative space-y-4 p-4">
      {/* Header row — page title + folder picker + page-toggle + action button */}
      <div className="grid w-full grid-cols-12 items-center gap-4">
        <div className="col-span-2 flex items-center gap-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-8 w-28" />
        </div>
        <div className="col-span-8 flex justify-center">
          <Skeleton className="h-10 w-64 rounded-lg" />
        </div>
        <div className="col-span-2 flex items-center justify-end">
          <Skeleton className="h-10 w-32" />
        </div>
      </div>

      {/* Sub-header row — filter pills + view toggle */}
      <div className="grid w-full grid-cols-12 items-center gap-4">
        <div className="col-span-2 flex items-center gap-4">
          <Skeleton className="h-8 w-56" />
        </div>
        <div className="col-span-8 flex justify-center">
          <Skeleton className="h-10 w-72 rounded-lg" />
        </div>
        <div className="col-span-2 flex items-center justify-end">
          <Skeleton className="h-10 w-48" />
        </div>
      </div>

      {/* Centered spinner pill — matches legacy "Loading Application…" */}
      <div className="pointer-events-none flex justify-center pt-16">
        <div className="flex items-center gap-3 rounded-lg border border-foreground/15 bg-foreground/[0.05] px-6 py-3 shadow-lg">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-[color:var(--brand-blue)] border-t-transparent" />
          <p className="text-sm font-medium text-foreground/80">Loading…</p>
        </div>
      </div>
    </div>
  );
}
